<template>
  <NText variant="caption1" weight="bold" class="pr-4">
    {{ t('common.recentRequests') }}
  </NText>

  <div
    v-if="isRevalidatingFrozenHistory"
    class="bg-neutral-secondary p-4 w-[27rem] h-[27rem] mt-1 flex justify-center relative bg-opacity-50 rounded-md m-auto"
  >
    <NLoading color="green" />
  </div>

  <div
    v-else
    class="bg-neutral-secondary p-4 max-h-[27rem] rounded-md mt-1 flex flex-col"
  >
    <NTable
      v-if="frozenHistory?.length"
      ref="loadMoreRef"
      :data="frozenHistory"
      cell-padding="small"
      :column-splitter="false"
      :striped="false"
      fixed-header
      identifier="requestId"
      class="mb-2 mt-1 w-[25rem] basis-[fit-content]"
    >
      <template #hoveredElements="{ row }">
        <NegarTrashLightIcon
          v-if="FROZEN_SYMBOLS_HISTORY_STATE.requested === row.requestStatesId"
          ref="deleteFreezeSymbolButtonRef"
          size="20"
          class="text-critical-primary ml-2 cursor-pointer"
          @click="handleDeleteFreeze(row)"
        />
      </template>

      <NTableColumn v-slot="{ row }">
        <NegarInfoWithBorderIcon
          size="20"
          class="cursor-pointer"
          @click="openFrozenSymbolInfoDialog(row)"
        />
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="`${t('modules.watchlist.symbol_name')}`"
        prop="name"
        sortable
        align="text-right"
      >
        <NText variant="caption2">
          {{ row.name }}
        </NText>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="`${t('modules.order.time')} ${t('withdrawal.submitRequest')}`"
        prop="requestDate"
        sortable
        align="text-right"
      >
        <NText variant="caption2" class="align-middle">
          {{ d(row.requestDate, 'shortDate') }} ,
        </NText>
        <NText variant="caption2" class="align-middle ms-1">
          {{ d(row.requestDate, 'shortTime') }}
        </NText>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('common.state')"
        prop="requestStateId"
        sortable
        align="text-right"
      >
        <NText variant="caption2" weight="medium">
          {{ row.requestState }}
        </NText>
      </NTableColumn>
    </NTable>

    <EmptyList
      v-else
      :title="t('common.noInformationAvailable')"
      title-class="text-center mt-3"
      body-class="!p-0"
      class="flex flex-col justify-center items-center rounded-md p-6"
    >
      <template #image>
        <Image src="empty_box" alt="not_found" class="w-28 h-max" />
      </template>
    </EmptyList>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { NegarTrashLightIcon, NegarInfoWithBorderIcon } from '@negar/icons';
import { useDialog, useToast } from '@negar/core';
import { FrozenHistoryItem } from '@libs/api-reference';
import {
  FROZEN_SYMBOLS_HISTORY_STATE,
  useGetFrozenHistory,
  useDeleteFreezeQuery,
  OPTION_QUERY_KEYS,
} from '@core/option';
import { useQueryClient } from '@libs/use';

import { useOptionDialogs } from '@/modules/option';

import EmptyList from '@/components/EmptyList.vue';
import Image from '@/components/Image.vue';
type TFrozenHistoryTableProps = {
  isFreeze: ComputedRef<boolean>;
};

const props = defineProps<TFrozenHistoryTableProps>();

const { t, d } = useI18n();
const toast = useToast();
const { remove: removeDialog } = useDialog();

const queryClient = useQueryClient();

const { frozenHistory, isRevalidatingFrozenHistory } = useGetFrozenHistory({
  isFreeze: computed(() => props.isFreeze.value),
});

const { openDeleteFreezeDialog, openFreezeInfoDialog } = useOptionDialogs();

const deleteFreezeSymbolButtonRef = ref();
const selectedFreezeSymbol = ref<FrozenHistoryItem | null>(null);

const { deleteFreezeSymbol, isPendingDeleteFreezeSymbol } =
  useDeleteFreezeQuery({
    onSuccess: () => {
      queryClient.invalidateQueries(
        OPTION_QUERY_KEYS.frozenHistory(toRef({ isFreeze: props.isFreeze }))
      );

      toast.neutral({
        message: t('option.cancelFreezeRequestMessage', {
          type: selectedFreezeSymbol.value?.isFreeze
            ? t('option.freeze')
            : t('option.unFreeze'),
          symbolName: selectedFreezeSymbol.value?.name,
        }),
        duration: 3000,
      });

      removeDialog();
    },
    onReject(error) {
      const message = error.response?.data[0].message;

      if (message) {
        toast.negative({
          message,
          duration: 3000,
        });
      }
    },
  });

const handleDeleteFreeze = (row: FrozenHistoryItem) => {
  selectedFreezeSymbol.value = row;

  openDeleteFreezeDialog({
    frozenSymbolItem: row,
    handleDelete: deleteFreezeSymbol,
    loading: computed(() => isPendingDeleteFreezeSymbol.value),
  });
};

const openFrozenSymbolInfoDialog = (row) => {
  openFreezeInfoDialog({
    frozenSymbolItem: row,
  });
};
</script>
